import React, { ChangeEvent } from 'react';

import { css } from '@emotion/react';
import {
  CssVars,
  FoundationIcon,
  NeutralColorToken,
  SelectionRow,
  SelectionRowRadioGroupProvider,
  SemanticColorToken,
  Toggle,
  Type,
} from '@robinhood/design';
import { FlattenReadonly } from '@x1/util-types';
import { useLazyLoadQuery } from 'react-relay';
import { graphql } from 'relay-runtime';

import {
  ShippingAddressPageShipmentOptionsQuery$data,
  ShippingAddressPageShipmentOptionsQuery as ShippingAddressPageShipmentOptionsQueryType,
} from 'src/clients/Network/gql/build/ShippingAddressPageShipmentOptionsQuery.graphql';
import { AddressInput } from 'src/clients/Network/gql/types/address';
import Button from 'src/components/creditApplication/Button';
import Cell from 'src/components/creditApplication/Cell';
import CreditApplicationPageLayout from 'src/components/creditApplication/CreditApplicationPageLayout';
import MapThumbnail from 'src/components/creditApplication/MapThumbnail';
import { CREDIT_APPLICATION_PAGE_MAX_WIDTH } from 'src/components/creditApplication/constants/dimensions';
import { useIsNfaStanford } from 'src/hooks/useAttribution';
import { AddressInputToAddressType } from 'src/utils/address';

const ShippingAddressPageShipmentOptionsQuery = graphql`
  query ShippingAddressPageShipmentOptionsQuery {
    shipmentOptions {
      id
      name
      description
      carrier
      deliveryMethod
      logoUrl
      feeAmountMicro
      canRequireSignature
      eta
      etaEnd
    }
  }
`;

export type ShippingOptionType = FlattenReadonly<ShippingAddressPageShipmentOptionsQuery$data['shipmentOptions']>;
interface IPropsIface {
  address: AddressInput;
  signatureRequired: boolean;
  shipmentOption?: ShippingOptionType;
  onChangeShipmentOption: (shipmentOption?: ShippingOptionType) => void;
  onEditShippingAddress: () => void;
  onChangeSignatureRequired: (signatureRequired: boolean) => void;
  onContinue: (address?: AddressInput) => void;
  continueLoading: boolean;
}

const ShippingAddressPage = ({
  address,
  signatureRequired,
  shipmentOption,
  onChangeShipmentOption,
  onEditShippingAddress,
  onChangeSignatureRequired,
  onContinue,
  continueLoading,
}: IPropsIface) => {
  const { shipmentOptions } = useLazyLoadQuery<ShippingAddressPageShipmentOptionsQueryType>(
    ShippingAddressPageShipmentOptionsQuery,
    {},
    { fetchPolicy: 'store-or-network' },
  );
  const [selectedOption, setSelectedOption] = React.useState<ShippingOptionType | undefined>(
    shipmentOption ?? shipmentOptions.at(0) ?? undefined,
  );
  const isNfaStanford = useIsNfaStanford();
  const subtitle = isNfaStanford
    ? 'For delivery during the school year, use your current mailing address—on or off campus.'
    : 'Your card will ship to the address below';

  const buttonLabel = React.useMemo(() => {
    return `Ship with ${selectedOption?.name}`;
  }, [selectedOption]);

  const onChangeShippingMethod = React.useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const option = shipmentOptions.find((opt) => opt.deliveryMethod === e.target.value);
      setSelectedOption(option);

      if (option == null) {
        return;
      }
      onChangeShipmentOption(option);
    },
    [shipmentOptions, onChangeShipmentOption],
  );

  // if no shipment option is provided, set the USPS option as the default
  React.useEffect(() => {
    if (shipmentOption != null) return;
    const uspsOption = shipmentOptions.find((opt) => opt.carrier === 'USPS');
    setSelectedOption(uspsOption);
    onChangeShipmentOption(uspsOption);
  }, [shipmentOption, shipmentOptions]);

  return (
    <CreditApplicationPageLayout title='Your shipping address' subtitle={subtitle}>
      <div css={root}>
        <div css={contentContainer}>
          <MapThumbnail
            address={AddressInputToAddressType(address)}
            alt='Shipping Address Thumbnail'
            width={CREDIT_APPLICATION_PAGE_MAX_WIDTH}
            height={400}
          />
          <Cell css={cellStyle} onClick={onEditShippingAddress}>
            <div css={editAddressContainer}>
              <FoundationIcon type='Edit12' color={SemanticColorToken.Positive} />
              <Type.Text size='M' isBold color={SemanticColorToken.Positive}>
                Edit shipping address
              </Type.Text>
            </div>
          </Cell>
        </div>
        <div>
          <SelectionRowRadioGroupProvider
            value={selectedOption?.deliveryMethod ?? null}
            onChange={onChangeShippingMethod}
          >
            {shipmentOptions.map((method, index) => (
              <div key={index}>
                <SelectionRow
                  startElem='image'
                  startImageSrc={`${method.logoUrl}?theme=gold`}
                  title={method.name}
                  caption={method.description}
                  variant='radioButton'
                  value={method.deliveryMethod}
                />
              </div>
            ))}
          </SelectionRowRadioGroupProvider>
          {selectedOption?.canRequireSignature && (
            <div css={toggleContainer}>
              <Type.Text size='M' color={NeutralColorToken.Fg1}>
                Delivery signature
              </Type.Text>
              <Toggle
                isChecked={signatureRequired}
                onChange={({ isChecked }) => onChangeSignatureRequired(isChecked)}
                showOnOffLabels={false}
              />
            </div>
          )}
        </div>
        <Button
          label={buttonLabel}
          type='button'
          width='full'
          onClick={() => onContinue(address)}
          state={continueLoading ? 'loading' : false}
        />
      </div>
    </CreditApplicationPageLayout>
  );
};

const root = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 40px;
`;

const contentContainer = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const editAddressContainer = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const cellStyle = css`
  padding: 16px;
  cursor: pointer;

  &:hover {
    background-color: ${CssVars.neutral.bg3};
  }
`;

const toggleContainer = css`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid ${CssVars.neutral.bg3};
  padding-top: 16px;
`;

export default ShippingAddressPage;
